"use client"

import { useEffect } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { pageReadyTiming, performanceRoute } from "@/lib/page-performance"
import { posthog } from "@/lib/posthog-client"

/** Call in the component containing real data, below its loading boundary. */
export function usePageReady(area: string) {
  const pathname = usePathname()
  const search = useSearchParams().toString()
  useEffect(() => {
    let paint = 0
    const frame = requestAnimationFrame(() => {
      paint = requestAnimationFrame(() => {
        const timing = pageReadyTiming(area)
        if (timing && document.visibilityState === "visible") {
          posthog.capture("page_content_ready", { ...timing, route: performanceRoute(pathname) })
        }
      })
    })
    return () => { cancelAnimationFrame(frame); cancelAnimationFrame(paint) }
  }, [area, pathname, search])
}

/** One worst slow interaction per route/visibility interval. No text, input
 * values, hrefs, or DOM IDs are captured. Complements PostHog's actual INP. */
export function SlowInteractionObserver() {
  const pathname = usePathname()
  useEffect(() => {
    if (typeof PerformanceObserver === "undefined" || !PerformanceObserver.supportedEntryTypes?.includes("event")) return
    let worst: Record<string, string | number | null> | undefined
    const observer = new PerformanceObserver((list) => {
      for (const event of list.getEntries() as (PerformanceEventTiming & { interactionId?: number })[]) {
        if (!event.interactionId || event.duration < 200 || (worst && event.duration <= Number(worst.duration_ms))) continue
        const target = event.target instanceof Element ? event.target : null
        worst = {
          route: performanceRoute(pathname), duration_ms: event.duration,
          input_delay_ms: Math.round(event.processingStart - event.startTime),
          processing_ms: Math.round(event.processingEnd - event.processingStart),
          presentation_ms: Math.round(Math.max(0, event.startTime + event.duration - event.processingEnd)),
          event_type: event.name, target_tag: target?.tagName.toLowerCase() ?? null,
          target_role: target?.getAttribute("role") ?? null,
          action: target?.closest("[data-perf-action]")?.getAttribute("data-perf-action") ?? null,
        }
      }
    })
    const options: PerformanceObserverInit & { durationThreshold: number } = { type: "event", durationThreshold: 200 }
    observer.observe(options)
    const flush = () => { if (worst) { posthog.capture("slow_interaction", worst); worst = undefined } }
    const hide = () => { if (document.visibilityState === "hidden") flush() }
    document.addEventListener("visibilitychange", hide)
    window.addEventListener("pagehide", flush)
    return () => { observer.disconnect(); flush(); document.removeEventListener("visibilitychange", hide); window.removeEventListener("pagehide", flush) }
  }, [pathname])
  return null
}
